import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Theme, ThemeContextType, UserThemePreferences } from '../types/theme';
import { defaultThemes, getDefaultTheme, getThemeById } from '../themes/presets';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../services/supabase';

// Default user preferences
const defaultPreferences: UserThemePreferences = {
  currentTheme: 'default',
  darkMode: false,
  fontSize: 'medium',
  reducedMotion: false,
  highContrast: false,
  compactMode: false
};

// Create the context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Local storage keys
const THEME_STORAGE_KEY = 'insightflow-theme-preferences';
const CUSTOM_THEMES_STORAGE_KEY = 'insightflow-custom-themes';

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user, supabaseUser } = useAuthStore();
  const [currentTheme, setCurrentTheme] = useState<Theme>(getDefaultTheme());
  const [userPreferences, setUserPreferences] = useState<UserThemePreferences>(defaultPreferences);
  const [availableThemes, setAvailableThemes] = useState<Theme[]>(defaultThemes);
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to lighten a color
  const lightenColor = (color: string, amount: number): string => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const newR = Math.min(255, Math.floor(r + (255 - r) * amount));
    const newG = Math.min(255, Math.floor(g + (255 - g) * amount));
    const newB = Math.min(255, Math.floor(b + (255 - b) * amount));
    
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Helper function to darken a color
  const darkenColor = (color: string, amount: number): string => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const newR = Math.max(0, Math.floor(r * (1 - amount)));
    const newG = Math.max(0, Math.floor(g * (1 - amount)));
    const newB = Math.max(0, Math.floor(b * (1 - amount)));
    
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Apply CSS custom properties to document root
  const applyCSSVariables = useCallback((theme: Theme, preferences: UserThemePreferences) => {
    const root = document.documentElement;
    
    // Apply primary color palette with proper gradients
    const primaryBase = theme.colors.primary;
    root.style.setProperty('--primary-50', lightenColor(primaryBase, 0.9));
    root.style.setProperty('--primary-100', lightenColor(primaryBase, 0.8));
    root.style.setProperty('--primary-200', lightenColor(primaryBase, 0.6));
    root.style.setProperty('--primary-300', lightenColor(primaryBase, 0.4));
    root.style.setProperty('--primary-400', lightenColor(primaryBase, 0.2));
    root.style.setProperty('--primary-500', primaryBase);
    root.style.setProperty('--primary-600', darkenColor(primaryBase, 0.1));
    root.style.setProperty('--primary-700', darkenColor(primaryBase, 0.2));
    root.style.setProperty('--primary-800', darkenColor(primaryBase, 0.3));
    root.style.setProperty('--primary-900', darkenColor(primaryBase, 0.4));

    // Apply secondary color palette with proper gradients
    const secondaryBase = theme.colors.secondary;
    root.style.setProperty('--secondary-50', lightenColor(secondaryBase, 0.9));
    root.style.setProperty('--secondary-100', lightenColor(secondaryBase, 0.8));
    root.style.setProperty('--secondary-200', lightenColor(secondaryBase, 0.6));
    root.style.setProperty('--secondary-300', lightenColor(secondaryBase, 0.4));
    root.style.setProperty('--secondary-400', lightenColor(secondaryBase, 0.2));
    root.style.setProperty('--secondary-500', secondaryBase);
    root.style.setProperty('--secondary-600', darkenColor(secondaryBase, 0.1));
    root.style.setProperty('--secondary-700', darkenColor(secondaryBase, 0.2));
    root.style.setProperty('--secondary-800', darkenColor(secondaryBase, 0.3));
    root.style.setProperty('--secondary-900', darkenColor(secondaryBase, 0.4));

    // Apply success color palette
    root.style.setProperty('--success-50', '#f0fdf4');
    root.style.setProperty('--success-100', '#dcfce7');
    root.style.setProperty('--success-200', '#bbf7d0');
    root.style.setProperty('--success-300', '#86efac');
    root.style.setProperty('--success-400', '#4ade80');
    root.style.setProperty('--success-500', theme.colors.success);
    root.style.setProperty('--success-600', theme.colors.success);
    root.style.setProperty('--success-700', theme.colors.success);
    root.style.setProperty('--success-800', theme.colors.success);
    root.style.setProperty('--success-900', theme.colors.success);

    // Apply warning color palette
    root.style.setProperty('--warning-50', '#fffbeb');
    root.style.setProperty('--warning-100', '#fef3c7');
    root.style.setProperty('--warning-200', '#fde68a');
    root.style.setProperty('--warning-300', '#fcd34d');
    root.style.setProperty('--warning-400', '#fbbf24');
    root.style.setProperty('--warning-500', theme.colors.warning);
    root.style.setProperty('--warning-600', theme.colors.warning);
    root.style.setProperty('--warning-700', theme.colors.warning);
    root.style.setProperty('--warning-800', theme.colors.warning);
    root.style.setProperty('--warning-900', theme.colors.warning);

    // Apply danger color palette
    root.style.setProperty('--danger-50', '#fef2f2');
    root.style.setProperty('--danger-100', '#fee2e2');
    root.style.setProperty('--danger-200', '#fecaca');
    root.style.setProperty('--danger-300', '#fca5a5');
    root.style.setProperty('--danger-400', '#f87171');
    root.style.setProperty('--danger-500', theme.colors.error);
    root.style.setProperty('--danger-600', theme.colors.error);
    root.style.setProperty('--danger-700', theme.colors.error);
    root.style.setProperty('--danger-800', theme.colors.error);
    root.style.setProperty('--danger-900', theme.colors.error);

    // Apply background and surface colors to both custom and Ionic variables
    root.style.setProperty('--ion-background-color', theme.colors.background);
    root.style.setProperty('--ion-text-color', theme.colors.onBackground);
    root.style.setProperty('--ion-toolbar-background', theme.colors.surface);
    root.style.setProperty('--ion-toolbar-color', theme.colors.onSurface);
    root.style.setProperty('--ion-item-background', theme.colors.surface);
    root.style.setProperty('--ion-card-background', theme.colors.surfaceVariant);
    
    // Also set our custom color variables for components that use them
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-secondary', theme.colors.secondary);
    root.style.setProperty('--color-background', theme.colors.background);
    root.style.setProperty('--color-surface', theme.colors.surface);
    root.style.setProperty('--color-on-primary', theme.colors.onPrimary);
    root.style.setProperty('--color-on-secondary', theme.colors.onSecondary);
    root.style.setProperty('--color-on-background', theme.colors.onBackground);
    root.style.setProperty('--color-on-surface', theme.colors.onSurface);
    root.style.setProperty('--color-on-surface-variant', theme.colors.onSurfaceVariant);
    root.style.setProperty('--color-border', theme.colors.border);
    root.style.setProperty('--color-divider', theme.colors.divider);

    // Force apply to body and ion-app for immediate visual feedback
    document.body.style.backgroundColor = theme.colors.background;
    document.body.style.color = theme.colors.onBackground;
    
    // Apply to ion-app if it exists
    const ionApp = document.querySelector('ion-app');
    if (ionApp) {
      (ionApp as HTMLElement).style.backgroundColor = theme.colors.background;
      (ionApp as HTMLElement).style.color = theme.colors.onBackground;
      (ionApp as HTMLElement).style.setProperty('--ion-background-color', theme.colors.background);
      (ionApp as HTMLElement).style.setProperty('--ion-text-color', theme.colors.onBackground);
    }

    // Force apply to ion-content elements
    const ionContents = document.querySelectorAll('ion-content');
    ionContents.forEach(content => {
      (content as HTMLElement).style.backgroundColor = theme.colors.background;
      (content as HTMLElement).style.setProperty('--background', theme.colors.background);
    });

    // Apply typography variables
    Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
      const adjustedSize = adjustFontSize(value, preferences.fontSize);
      root.style.setProperty(`--font-size-${key}`, adjustedSize);
    });

    Object.entries(theme.typography.fontWeight).forEach(([key, value]) => {
      root.style.setProperty(`--font-weight-${key}`, value.toString());
    });

    Object.entries(theme.typography.lineHeight).forEach(([key, value]) => {
      root.style.setProperty(`--line-height-${key}`, value.toString());
    });

    // Apply font families
    root.style.setProperty('--font-family-primary', theme.typography.fontFamily.primary);
    root.style.setProperty('--font-family-secondary', theme.typography.fontFamily.secondary);
    root.style.setProperty('--font-family-mono', theme.typography.fontFamily.mono);

    // Apply spacing variables
    Object.entries(theme.spacing).forEach(([key, value]) => {
      const adjustedSpacing = adjustSpacing(value, preferences.compactMode);
      root.style.setProperty(`--spacing-${key}`, adjustedSpacing);
    });

    // Apply border radius variables
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--border-radius-${key}`, value);
    });

    // Apply shadow variables
    Object.entries(theme.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });

    // Apply breakpoint variables
    Object.entries(theme.breakpoints).forEach(([key, value]) => {
      root.style.setProperty(`--breakpoint-${key}`, value);
    });

    // Apply accessibility preferences
    if (preferences.reducedMotion) {
      root.style.setProperty('--animation-duration', '0.01ms');
      root.style.setProperty('--transition-duration', '0.01ms');
      root.classList.add('reduced-motion');
    } else {
      root.style.setProperty('--animation-duration', '300ms');
      root.style.setProperty('--transition-duration', '200ms');
      root.classList.remove('reduced-motion');
    }

    // Apply high contrast if needed
    if (preferences.highContrast) {
      root.style.setProperty('--primary-500', '#000000');
      root.style.setProperty('--primary-600', '#000000');
      root.style.setProperty('--ion-text-color', '#000000');
      root.style.setProperty('--ion-background-color', '#ffffff');
      root.classList.add('high-contrast');
      document.body.style.backgroundColor = '#ffffff';
      document.body.style.color = '#000000';
    } else {
      root.classList.remove('high-contrast');
    }

    // Apply font size preferences
    const fontSizeMultiplier = preferences.fontSize === 'small' ? 0.875 : 
                              preferences.fontSize === 'large' ? 1.125 : 1;
    root.style.setProperty('--font-size-multiplier', fontSizeMultiplier.toString());
    root.style.fontSize = `${fontSizeMultiplier * 16}px`;

    // Apply compact mode
    if (preferences.compactMode) {
      root.style.setProperty('--spacing-multiplier', '0.75');
      root.classList.add('compact-mode');
    } else {
      root.style.setProperty('--spacing-multiplier', '1');
      root.classList.remove('compact-mode');
    }

    // Set dark mode class and apply dark colors
    if (theme.isDark || preferences.darkMode) {
      root.classList.add('dark');
      document.body.classList.add('dark');
      
      // Override with dark colors
      root.style.setProperty('--ion-background-color', '#1f2937');
      root.style.setProperty('--ion-text-color', '#f9fafb');
      document.body.style.backgroundColor = '#1f2937';
      document.body.style.color = '#f9fafb';
      
      // Update CSS variables for dark mode with proper color variations
      root.style.setProperty('--primary-50', darkenColor(theme.colors.primary, 0.8));
      root.style.setProperty('--primary-100', darkenColor(theme.colors.primary, 0.6));
      root.style.setProperty('--primary-400', lightenColor(theme.colors.primary, 0.2));
      root.style.setProperty('--secondary-400', lightenColor(theme.colors.secondary, 0.2));
      
      // Apply dark mode colors to all elements
      const allElements = document.querySelectorAll('*');
      allElements.forEach(element => {
        const htmlElement = element as HTMLElement;
        
        // Apply dark mode to ion-app and ion-content
        if (element.tagName === 'ION-APP' || element.tagName === 'ION-CONTENT') {
          htmlElement.style.backgroundColor = '#1f2937';
          htmlElement.style.color = '#f9fafb';
          htmlElement.style.setProperty('--background', '#1f2937');
          htmlElement.style.setProperty('--color', '#f9fafb');
        }
        
        // Apply dark mode to cards and items
        if (element.tagName === 'ION-CARD' || element.tagName === 'ION-ITEM') {
          htmlElement.style.setProperty('--background', '#374151');
          htmlElement.style.setProperty('--color', '#f9fafb');
        }
        
        // Apply dark mode to toolbars
        if (element.tagName === 'ION-TOOLBAR') {
          htmlElement.style.setProperty('--background', '#374151');
          htmlElement.style.setProperty('--color', '#f9fafb');
        }
      });
    } else {
      root.classList.remove('dark');
      document.body.classList.remove('dark');
      
      // Remove dark mode styles when switching back to light mode
      const allElements = document.querySelectorAll('*');
      allElements.forEach(element => {
        const htmlElement = element as HTMLElement;
        
        // Reset styles for ion elements
        if (element.tagName === 'ION-APP' || element.tagName === 'ION-CONTENT') {
          htmlElement.style.backgroundColor = '';
          htmlElement.style.color = '';
          htmlElement.style.removeProperty('--background');
          htmlElement.style.removeProperty('--color');
        }
        
        if (element.tagName === 'ION-CARD' || element.tagName === 'ION-ITEM') {
          htmlElement.style.removeProperty('--background');
          htmlElement.style.removeProperty('--color');
        }
        
        if (element.tagName === 'ION-TOOLBAR') {
          htmlElement.style.removeProperty('--background');
          htmlElement.style.removeProperty('--color');
        }
      });
    }

    // Set compact mode class
    if (preferences.compactMode) {
      root.classList.add('compact');
      document.body.classList.add('compact');
    } else {
      root.classList.remove('compact');
      document.body.classList.remove('compact');
    }

    console.log('Applied theme:', theme.name, 'with colors:', theme.colors);
  }, []);

  // Adjust font size based on user preference
  const adjustFontSize = (baseSize: string, sizePreference: 'small' | 'medium' | 'large'): string => {
    const multipliers = { small: 0.875, medium: 1, large: 1.125 };
    const size = parseFloat(baseSize);
    const unit = baseSize.replace(size.toString(), '');
    return `${size * multipliers[sizePreference]}${unit}`;
  };

  // Adjust spacing for compact mode
  const adjustSpacing = (baseSpacing: string, isCompact: boolean): string => {
    if (!isCompact) return baseSpacing;
    const size = parseFloat(baseSpacing);
    const unit = baseSpacing.replace(size.toString(), '');
    return `${size * 0.75}${unit}`;
  };

  // Load preferences from database or local storage
  const loadPreferences = useCallback(async () => {
    setIsLoading(true);
    
    try {
      if (user && supabaseUser) {
        // Load from database
        const { data, error } = await supabase
          .from('user_preferences')
          .select('*')
          .eq('user_id', supabaseUser.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading user preferences:', error);
          throw error;
        }

        if (data) {
          const dbPreferences: UserThemePreferences = {
            currentTheme: data.theme_id || defaultPreferences.currentTheme,
            darkMode: data.dark_mode ?? defaultPreferences.darkMode,
            fontSize: data.font_size || defaultPreferences.fontSize,
            reducedMotion: data.reduced_motion ?? defaultPreferences.reducedMotion,
            highContrast: data.high_contrast ?? defaultPreferences.highContrast,
            compactMode: data.compact_mode ?? defaultPreferences.compactMode,
            customColors: data.custom_colors ? JSON.parse(data.custom_colors) : undefined
          };
          
          setUserPreferences(dbPreferences);
          
          // Set theme based on preferences
          const theme = getThemeById(dbPreferences.currentTheme) || getDefaultTheme();
          setCurrentTheme(theme);
          
          return;
        }
      }

      // Fallback to local storage
      const storedPreferences = localStorage.getItem(THEME_STORAGE_KEY);
      if (storedPreferences) {
        const parsed = JSON.parse(storedPreferences) as UserThemePreferences;
        setUserPreferences(parsed);
        
        const theme = getThemeById(parsed.currentTheme) || getDefaultTheme();
        setCurrentTheme(theme);
      } else {
        // Use system preference for dark mode
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const initialPreferences = {
          ...defaultPreferences,
          darkMode: prefersDark
        };
        
        setUserPreferences(initialPreferences);
        setCurrentTheme(getDefaultTheme());
      }
    } catch (error) {
      console.error('Error loading theme preferences:', error);
      setUserPreferences(defaultPreferences);
      setCurrentTheme(getDefaultTheme());
    } finally {
      setIsLoading(false);
    }
  }, [user, supabaseUser]);

  // Save preferences to database or local storage
  const savePreferences = useCallback(async (preferences: UserThemePreferences) => {
    try {
      if (user && supabaseUser) {
        // Save to database
        const { error } = await supabase
          .from('user_preferences')
          .upsert({
            user_id: supabaseUser.id,
            theme_id: preferences.currentTheme,
            dark_mode: preferences.darkMode,
            font_size: preferences.fontSize,
            reduced_motion: preferences.reducedMotion,
            high_contrast: preferences.highContrast,
            compact_mode: preferences.compactMode,
            custom_colors: preferences.customColors ? JSON.stringify(preferences.customColors) : null,
            updated_at: new Date().toISOString()
          });

        if (error) {
          console.error('Error saving user preferences:', error);
          throw error;
        }
      } else {
        // Save to local storage
        localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(preferences));
      }
    } catch (error) {
      console.error('Error saving theme preferences:', error);
      // Always save to local storage as fallback
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(preferences));
    }
  }, [user, supabaseUser]);

  // Load custom themes from local storage
  const loadCustomThemes = useCallback(() => {
    try {
      const storedCustomThemes = localStorage.getItem(CUSTOM_THEMES_STORAGE_KEY);
      if (storedCustomThemes) {
        const customThemes = JSON.parse(storedCustomThemes) as Theme[];
        setAvailableThemes([...defaultThemes, ...customThemes]);
      }
    } catch (error) {
      console.error('Error loading custom themes:', error);
    }
  }, []);

  // Set theme
  const setTheme = useCallback(async (themeId: string) => {
    const theme = getThemeById(themeId) || availableThemes.find(t => t.id === themeId);
    if (!theme) return;

    setCurrentTheme(theme);
    
    const newPreferences = {
      ...userPreferences,
      currentTheme: themeId,
      darkMode: theme.isDark
    };
    
    setUserPreferences(newPreferences);
    await savePreferences(newPreferences);
  }, [userPreferences, availableThemes, savePreferences]);

  // Update preferences
  const updatePreferences = useCallback(async (newPreferences: Partial<UserThemePreferences>) => {
    const updatedPreferences = { ...userPreferences, ...newPreferences };
    setUserPreferences(updatedPreferences);
    await savePreferences(updatedPreferences);

    // If theme changed, update current theme
    if (newPreferences.currentTheme && newPreferences.currentTheme !== userPreferences.currentTheme) {
      const theme = getThemeById(newPreferences.currentTheme) || getDefaultTheme();
      setCurrentTheme(theme);
    }

    // Force re-apply CSS variables for immediate effect
    if (currentTheme) {
      applyCSSVariables(currentTheme, updatedPreferences);
    }
  }, [userPreferences, savePreferences, currentTheme, applyCSSVariables]);

  // Create custom theme
  const createCustomTheme = useCallback((themeData: Partial<Theme>) => {
    const customTheme: Theme = {
      id: `custom-${Date.now()}`,
      name: themeData.name || 'Custom Theme',
      description: themeData.description || 'Custom user theme',
      colors: { ...getDefaultTheme().colors, ...themeData.colors },
      typography: { ...getDefaultTheme().typography, ...themeData.typography },
      spacing: { ...getDefaultTheme().spacing, ...themeData.spacing },
      borderRadius: { ...getDefaultTheme().borderRadius, ...themeData.borderRadius },
      shadows: { ...getDefaultTheme().shadows, ...themeData.shadows },
      breakpoints: { ...getDefaultTheme().breakpoints, ...themeData.breakpoints },
      isDark: themeData.isDark ?? false,
      isCustom: true
    };

    const updatedThemes = [...availableThemes, customTheme];
    setAvailableThemes(updatedThemes);

    // Save custom themes to local storage
    const customThemes = updatedThemes.filter(t => t.isCustom);
    localStorage.setItem(CUSTOM_THEMES_STORAGE_KEY, JSON.stringify(customThemes));

    return customTheme;
  }, [availableThemes]);

  // Reset theme to default
  const resetTheme = useCallback(async () => {
    const defaultTheme = getDefaultTheme();
    setCurrentTheme(defaultTheme);
    
    const resetPreferences = {
      ...defaultPreferences,
      currentTheme: defaultTheme.id
    };
    
    setUserPreferences(resetPreferences);
    await savePreferences(resetPreferences);
  }, [savePreferences]);

  // Load preferences on mount and when user changes
  useEffect(() => {
    loadPreferences();
    loadCustomThemes();
  }, [loadPreferences, loadCustomThemes]);

  // Apply CSS variables when theme or preferences change
  useEffect(() => {
    if (!isLoading && currentTheme) {
      console.log('Applying theme:', currentTheme.name, currentTheme.id);
      applyCSSVariables(currentTheme, userPreferences);
    }
  }, [currentTheme, userPreferences, isLoading, applyCSSVariables]);

  // Listen for system dark mode changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      if (!user) {
        // Only auto-update for non-authenticated users
        updatePreferences({ darkMode: e.matches });
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [user, updatePreferences]);

  const contextValue: ThemeContextType = {
    currentTheme,
    userPreferences,
    availableThemes,
    setTheme,
    updatePreferences,
    createCustomTheme,
    resetTheme,
    isLoading
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}; 